import { ComponentProps, FC, useMemo } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { RangeSlider } from '@telialt/common-range-slider/src';

import { FacetStats, FilterRange } from '../../model';
import { FacetItem } from '../FacetItem/FacetItem';
import { FacetContainer } from './Facets.styles';

const toWhole = (value: number | string) => Number(value)?.toFixed(0);

type SliderOverridableProps = Pick<
    ComponentProps<typeof RangeSlider>,
    'minValue' | 'maxValue' | 'formatLabel' | 'inputAdornment'
>;

type Props = {
    facet: FacetStats;
    filter?: FilterRange;
    onChange: (fieldName: string, filter?: FilterRange) => void;
};

export const StatsFacet: FC<Props> = ({ facet, filter, onChange }) => {
    const { t } = useTranslations();

    const sliderOverrides: Partial<SliderOverridableProps> = useMemo(() => {
        switch (facet.key) {
            case 'weightGrams':
                return {
                    formatLabel: (value: number) => `${value / 1000}`,
                    inputAdornment: t['search.component.weight_filter.unit'],
                };
            case 'price':
                return {
                    minValue: Math.floor(facet.min),
                    maxValue: Math.ceil(facet.max),
                    formatLabel: toWhole,
                    inputAdornment: t['search.component.price_filter.unit'],
                };
            case 'dataSizeDomesticTotal':
            case 'dataSizeEuropeTotal':
            case 'dataSizeTravelDestinationTotal':
            case 'dataSizeDomesticStandard':
                return {
                    minValue: Math.floor(facet.min),
                    maxValue: Math.ceil(facet.max),
                    formatLabel: (value: number) => {
                        if (value >= 9999) {
                            return t['search.component.data.unlimited_label'];
                        }

                        return `${value}`;
                    },
                    inputAdornment: t['search.component.data.gb_unit'],
                };
            default:
                return {};
        }
    }, [facet.key, facet.max, facet.min, t]);

    const handleRangeChange = ([from, to]: [number, number]) => {
        onChange(facet.key, { gte: from, lte: to });
    };

    const minValue = sliderOverrides.minValue ?? facet.min;
    const maxValue = sliderOverrides.maxValue ?? facet.max;
    const formatLabel = sliderOverrides.formatLabel || undefined;
    const inputAdornment = sliderOverrides.inputAdornment || undefined;

    return (
        <FacetItem id={facet.key} label={facet.label} facetType="stats">
            <FacetContainer>
                <RangeSlider
                    minValue={minValue}
                    maxValue={maxValue}
                    fromValue={filter?.gte}
                    toValue={filter?.lte}
                    ariaLabel={facet.label}
                    onChange={handleRangeChange}
                    formatLabel={formatLabel}
                    inputAdornment={inputAdornment}
                />
            </FacetContainer>
        </FacetItem>
    );
};
